import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "input", "search", "option", "noList", "clearButton", "emptyState", "header"]
  static values = { listId: Number }

  connect() {
    this.element.addEventListener('show.bs.dropdown', this.focusSearch.bind(this))

    if (this.listIdValue) {
      this.clearButtonTarget.classList.remove("d-none")
    }
  }

  focusSearch() {
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  search() {
    const searchValue = this.searchTarget.value.toLowerCase()
    const options = this.optionTargets
    const dividers = this.element.querySelectorAll('.dropdown-divider')

    // Track how many visible options each folder header has
    const headerOptionCounts = new Map()
    let totalVisibleOptions = 0
    let visibleFolderlessOptions = 0
    let visibleFolderOptions = 0

    this.headerTargets.forEach(header => headerOptionCounts.set(header, 0))

    options.forEach(option => {
      const listNameSpan = option.querySelector('.list-select-item__name')
      if (!listNameSpan) return

      const isVisible = listNameSpan.textContent.toLowerCase().includes(searchValue)
      option.classList.toggle("d-none", !isVisible)

      if (isVisible) {
        totalVisibleOptions++

        // Determine if this option belongs to a folder or is folderless
        const folderName = option.dataset.folderName
        if (folderName && folderName.trim() !== "") {
          const header = this.headerTargets.find(h => h.dataset.folderName === folderName)
          if (header) {
            headerOptionCounts.set(header, (headerOptionCounts.get(header) || 0) + 1)
            visibleFolderOptions++
          }
        } else {
          visibleFolderlessOptions++
        }
      }
    })

    // Hide folder headers that have no visible options
    this.headerTargets.forEach(header => {
      header.classList.toggle("d-none", !headerOptionCounts.get(header))
    })

    // Only show divider when there are both folder and folderless options visible
    dividers.forEach(divider => {
      divider.classList.toggle("d-none", !(visibleFolderlessOptions > 0 && visibleFolderOptions > 0))
    })

    this.emptyStateTarget.classList.toggle("d-none", totalVisibleOptions > 0 || searchValue === "")
  }

  clearList() {
    this.#clearList()
    this.#closeDropdown()
  }

  setList({ params: { listId } }) {
    this.#setList(listId)
    this.#closeDropdown()
  }

  #setList(listId) {
    // Clear search and update UI to show all options
    this.searchTarget.value = ""
    this.search()

    // Convert string IDs to integers for comparison
    const selectedOption = this.optionTargets.find(option =>
      parseInt(option.dataset.id) === parseInt(listId)
    )
    const listName = selectedOption.dataset.name
    const folderName = selectedOption.dataset.folderName

    // Generate different HTML structure based on whether list has a folder
    const displayText = this.#createDisplayText(listName, folderName)

    this.buttonTarget.innerHTML = displayText
    this.inputTarget.value = listId
    this.listIdValue = listId
    this.clearButtonTarget.classList.remove("d-none")
  }

    // TODO: may as well create an erb partial instead
  #createDisplayText(listName, folderName) {
    if (!folderName || folderName.trim() === "") {
      return `<span>${listName}</span>`
    }

    const folderIcon = this.#getFolderIcon(folderName)
    return `
      <span class="list-select__current">
        ${folderIcon}
        <span class="text-hint">${folderName}</span>
        <span class="text-ghost"> /</span>
        <span>${listName}</span>
      </span>
    `.trim()
  }

  #getFolderIcon(folderName) {
    // Find the folder header that matches this folder name
    const header = this.headerTargets.find(header =>
      header.dataset.folderName === folderName
    )
    // Extract the icon HTML from the header, or return empty string if not found
    return header?.querySelector('.app-main-submenu__icon')?.outerHTML || ''
  }

  #clearList() {
    this.buttonTarget.innerHTML = this.noListTarget.innerHTML
    this.inputTarget.value = ""
    this.listIdValue = null
    this.clearButtonTarget.classList.add("d-none")
  }

  #closeDropdown() {
    this.element.click()
  }
}
